import * as React from 'react';
import { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import useCrew from '@/src/Production/components/hooks/useCrew';

/* My gigs — offer cards with Accept/Decline, past list, calendar card
   (mock: crew-portal.html). */

const toWebcal = (url: string) => url.replace(/^https?/, 'webcal');
const toGoogleCal = (url: string) =>
    `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(toWebcal(url))}`;

export default function CrewGigsV2() {
    const { getAssignments, respondToAssignment, getCalendarFeed } = useCrew();
    const [data, setData] = useState<any | null>(null);
    const [feed, setFeed] = useState<any | null>(null);
    const [copied, setCopied] = useState(false);
    const [busy, setBusy] = useState<number | null>(null);

    const reload = async () => {
        setData(await getAssignments().catch(() => null));
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            const [assignments, calendarFeed] = await Promise.all([
                getAssignments().catch(() => null),
                getCalendarFeed().catch(() => null),
            ]);
            if (!mounted) return;
            setData(assignments);
            setFeed(calendarFeed);
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const respond = async (id: number, action: 'confirm' | 'decline') => {
        setBusy(id);
        await respondToAssignment(id, action).catch(() => null);
        setBusy(null);
        await reload();
    };

    const upcoming = data?.upcoming ?? [];
    const past = data?.past ?? [];
    const needsResponse = upcoming.filter(
        (a: any) => a.status === 'pending',
    ).length;

    const rate = (a: any) =>
        a.rate
            ? a.rateType === 'flat'
                ? `$${+a.rate} flat`
                : `$${+a.rate}/hr`
            : null;

    const timeRange = (a: any) => {
        const start = a.assignmentStart ?? a.startAt;
        const end = a.assignmentEnd ?? a.endAt;
        if (!start) return 'Time TBD';
        const startMoment = moment(start).tz(a.timezone || moment.tz.guess());
        const endMoment = end
            ? moment(end).tz(a.timezone || moment.tz.guess())
            : null;
        return `${startMoment.format('h:mm A')}${endMoment ? ` – ${endMoment.format('h:mm A')}` : ''}`;
    };

    return (
        <main>
            <div className="ps-label mb-1 mt-3.5">My assignments</div>
            <h1 className="m-0 mb-5 text-[20px] font-semibold tracking-[-0.02em]">
                {data?.staff
                    ? `Hi ${data.staff.firstName}${needsResponse ? ` — ${needsResponse} need${needsResponse === 1 ? 's' : ''} a response` : ''}`
                    : 'My gigs'}
            </h1>

            {!data && <div className="text-[13px] text-faint">Loading…</div>}

            {data && upcoming.length === 0 && (
                <div className="rounded-2xl border border-dashed border-border p-4 text-[12.5px] text-muted-foreground">
                    No upcoming gigs — new offers land here (and in your
                    email).
                </div>
            )}

            {upcoming.map((a: any) => (
                <div
                    key={a.id}
                    className={
                        'mb-3 rounded-2xl border p-4 ' +
                        (a.status === 'pending'
                            ? 'border-brand/40'
                            : 'border-border')
                    }
                >
                    <div className="flex items-start gap-2.5">
                        <span className="w-12 flex-none rounded-[9px] bg-secondary py-1.5 text-center">
                            <span className="ps-label block">
                                {a.startAt || a.assignmentStart
                                    ? moment(
                                          a.assignmentStart ?? a.startAt,
                                      ).format('MMM')
                                    : '—'}
                            </span>
                            <span className="mt-px block text-[14px] font-semibold">
                                {a.startAt || a.assignmentStart
                                    ? moment(
                                          a.assignmentStart ?? a.startAt,
                                      ).format('D')
                                    : ''}
                            </span>
                        </span>
                        <div className="min-w-0 flex-1">
                            <div className="text-[14px] font-semibold tracking-[-0.01em]">
                                {a.name}
                            </div>
                            <div className="mt-0.5 text-[12px] leading-relaxed text-muted-foreground">
                                {a.role}
                                {rate(a) ? (
                                    <>
                                        {' · '}
                                        <b className="text-foreground">
                                            {rate(a)}
                                        </b>
                                    </>
                                ) : null}
                                <br />
                                {timeRange(a)}
                                {a.venueName ? ` · ${a.venueName}` : ''}
                            </div>
                        </div>
                        {a.status === 'pending' && (
                            <span className="flex-none rounded-full bg-brand-soft px-2.5 py-1 text-[10.5px] font-semibold text-brand-ink">
                                New
                            </span>
                        )}
                    </div>

                    {a.status === 'pending' ? (
                        <div className="mt-3.5 flex gap-2">
                            <button
                                type="button"
                                disabled={busy === +a.id}
                                onClick={() => respond(+a.id, 'confirm')}
                                className="flex h-[38px] flex-1 cursor-pointer items-center justify-center rounded-full border-0 bg-foreground font-[inherit] text-[13px] font-semibold text-background disabled:opacity-60"
                            >
                                Accept
                            </button>
                            <button
                                type="button"
                                disabled={busy === +a.id}
                                onClick={() => respond(+a.id, 'decline')}
                                className="flex h-[38px] flex-1 cursor-pointer items-center justify-center rounded-full border border-border bg-transparent font-[inherit] text-[13px] font-semibold text-muted-foreground disabled:opacity-60"
                            >
                                Decline
                            </button>
                        </div>
                    ) : (
                        <div
                            className={
                                'mt-3.5 flex items-center gap-2 text-[12.5px] font-semibold ' +
                                (a.status === 'confirmed'
                                    ? 'text-good'
                                    : 'text-attention')
                            }
                        >
                            {a.status === 'confirmed' ? (
                                <>
                                    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M4 10.5l4 4 8-9" />
                                    </svg>
                                    Confirmed
                                    {a.confirmedAt
                                        ? ` ${moment(a.confirmedAt).format('MMM D')}`
                                        : ''}{' '}
                                    · syncs to your calendar
                                </>
                            ) : (
                                'Declined'
                            )}
                        </div>
                    )}
                </div>
            ))}

            {past.length > 0 && (
                <section className="mt-7">
                    <div className="ps-label mb-1.5">Past</div>
                    {past.map((a: any) => (
                        <div
                            key={a.id}
                            className="flex items-center gap-3 border-t border-line py-2.5 text-[12.5px] first:border-t-0"
                        >
                            <span className="w-16 flex-none tabular-nums text-faint">
                                {a.startAt
                                    ? moment(a.startAt).format('MMM D')
                                    : '—'}
                            </span>
                            <span className="min-w-0 flex-1 truncate font-semibold">
                                {a.name}
                            </span>
                            <span className="flex-none text-faint">
                                {a.role}
                            </span>
                        </div>
                    ))}
                </section>
            )}

            <section className="mt-7">
                <div className="ps-label mb-1.5">Your calendar</div>
                <div className="rounded-2xl border border-dashed border-border p-4">
                    {!feed && (
                        <div className="text-[12.5px] text-faint">
                            Loading…
                        </div>
                    )}
                    {feed && (
                        <>
                            <a
                                href={toWebcal(feed.url)}
                                className="flex h-10 items-center justify-center rounded-full bg-foreground text-[13px] font-semibold text-background no-underline"
                            >
                                Add to iCal / Outlook
                            </a>
                            <a
                                href={toGoogleCal(feed.url)}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-2.5 flex h-10 items-center justify-center rounded-full border border-border text-[13px] font-semibold text-foreground no-underline"
                            >
                                Add to Google Calendar
                            </a>
                            <button
                                type="button"
                                onClick={async () => {
                                    await navigator.clipboard
                                        .writeText(feed.url)
                                        .catch(() => null);
                                    setCopied(true);
                                    setTimeout(() => setCopied(false), 2000);
                                }}
                                className="mt-3 block w-full cursor-pointer border-0 bg-transparent text-center font-[inherit] text-[12px] font-semibold text-faint hover:text-brand-ink"
                            >
                                {copied ? 'Copied' : 'or copy the feed link'}
                            </button>
                            <p className="mb-0 mt-2.5 text-center text-[11px] text-faint">
                                Confirmed gigs automatically sync to your
                                calendar.
                            </p>
                        </>
                    )}
                </div>
            </section>
        </main>
    );
}
